import configJSON from 'config.json';
import ExpCommercialTheme from './theme-config';
import { CountryCodeList } from 'types/countries';
import { SiteLanguage } from 'constants/locale';
import {
  EXP_COMMERCIAL_ROOT_DOMAIN,
} from 'constants/domains';
import {
  AREA_LISTINGS_ROUTE,
  EMBER_ROUTE,
  ROOT_ROUTE,
} from 'components/dynamic-page/route-matchers/types';
import { SEARCH_PAGE_ROUTE_NAME } from 'types/search_page_types';
import { createHeaderConfig } from './header-config';
import { createHomeConfig } from './home-config';
import { createFooterConfig } from './footer-config';
import { homeRouteHandler } from './route-handler';
import { agentSearchConfig as agentSearch } from './search-agent-config';
import { createQuoteConfig } from './quote-config';
import { Colors } from 'constants/colors';
import { benefitsConfig } from './benefits-config';
import { metricsConfig } from './metrics-config';

import {
  EXP_COMMERCIAL_TENANT_ID,
  EXP_COMMERCIAL_TENANT_ID_TYPE,
} from '@tenants/types';
import { searchOurPropertiesConfig } from './search-our-properties-config';
import { Namespaces } from 'types/locales';

import type {
  ContentHosts,
  FooterConfig,
  HeaderConfig,
  HomeConfig,
  Tenant,
  TenantConfigAgentSearch,
  TenantConfigContactUs,
  TenantConfigExpCommercial,
  TenantConfigFilters,
  TenantConfigFooter,
  TenantConfigHeader, 
  TenantConfigHome,
} from '@tenants/types';
import { createCommercialFaqData } from './faq-config';
import { contactUsConfig } from './contact-us-config';

//#region Public Constants

export const EXP_COMMERCIAL_L18N_NAMESPACES = [
  Namespaces.EXP_COMMERCIAL,
  Namespaces.COMMON,
  Namespaces.FORM_ERROR,
  Namespaces.MODAL,
] as const;

//#endregion

//#region Config Factories
const header: HeaderConfig = createHeaderConfig(EXP_COMMERCIAL_ROOT_DOMAIN);
const home: HomeConfig = createHomeConfig(EXP_COMMERCIAL_ROOT_DOMAIN);
const footer: FooterConfig = createFooterConfig(EXP_COMMERCIAL_ROOT_DOMAIN);
//#endregion

//#region Public Types
export type ExpCommercialTenantConfig = TenantConfigHeader & TenantConfigHome & TenantConfigFooter & TenantConfigAgentSearch & TenantConfigExpCommercial & TenantConfigFilters & TenantConfigContactUs;
export type ExpCommercialTenant = Tenant<EXP_COMMERCIAL_TENANT_ID_TYPE, typeof ExpCommercialTheme, ExpCommercialTenantConfig>;

//#endregion

/**
 * The eXp Commercial tenant.
 *
 * This tenant is used to render the eXp Commercial website.
 */
export const expCommercialTenant: ExpCommercialTenant = {
  id: EXP_COMMERCIAL_TENANT_ID,
  hosts: configJSON.hosts[EXP_COMMERCIAL_TENANT_ID],
  contentHosts: configJSON.contentPageApi[EXP_COMMERCIAL_TENANT_ID] as ContentHosts,
  blogApiUrl: configJSON.blogApi[EXP_COMMERCIAL_TENANT_ID],
  blogContentPageUrl: configJSON.blogContentPageUrl[EXP_COMMERCIAL_TENANT_ID],
  theme: ExpCommercialTheme,
  localization: {
    languages: [SiteLanguage.English],
    countries: [CountryCodeList.UNITED_STATES],
    namespaces: EXP_COMMERCIAL_L18N_NAMESPACES,
    defaultLanguage: SiteLanguage.English,
    defaultCountry: CountryCodeList.UNITED_STATES,
  },
  config: {
    header,
    home,
    footer,
    agentSearch,
    commercial: {
      quotes: {
        leo: createQuoteConfig(Colors.expCommercialGreen, 'highlight-green'),
      },
      metrics: metricsConfig,
      benefits: benefitsConfig,
      searchOurProperties: searchOurPropertiesConfig,
      faq: createCommercialFaqData(),
    },
    filters: {
      hideExpiredListings: true,
      countryCode: CountryCodeList.UNITED_STATES,
    },
    contactUs: contactUsConfig,
  },
  routes: {
    claimed: [ROOT_ROUTE, AREA_LISTINGS_ROUTE, SEARCH_PAGE_ROUTE_NAME],
    excluded: [EMBER_ROUTE],
    handlers: [
      {
        match: ROOT_ROUTE,
        handler: homeRouteHandler,
      },
    ],
  },
  debug: {
    forceTenantHeaderValue: `useTenant="${EXP_COMMERCIAL_TENANT_ID}"`,
    forceTenantFeatureValue: EXP_COMMERCIAL_TENANT_ID,
  },
  features: {
    MyLinkMyLead: false,
    AgentSearch: false,
    AccessibilityWidget: false,
  },
  googleTagManager: {
    containerIds: {
      defaultContainerId: configJSON.googleTagManager.containerId, // TODO: Add the correct container ID
    },
  },
  sso: {
    googleWebClientId: process.env.NEXT_PUBLIC_GOOGLE_WEB_CLIENT_ID || '', // TODO: Add the correct client ID
    facebookAppId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID || '', // TODO: Add the correct app ID
  },
  sitemap: {
    url: [
      'https://www.expcommercial.com/sitemaps/area-pages/sitemap.xml',
      'https://www.expcommercial.com/sitemaps/address-pages/sitemap.xml',
      'https://www.expcommercial.com/sitemaps/root-pages/sitemap-index.xml',
    ],
  },
  pvr: {
    phoneNumber: '1-844-539-0722',
  },
};
